import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HistoryEduRoundedIcon from "@mui/icons-material/HistoryEduRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";

import { Draft, Feedback, ReferenceWbr, Team } from "../types";
import { formatDateTime, relativeTime } from "../utils/format";

interface DraftsProps {
  team: Team;
  drafts: Draft[];
  feedback: Feedback[];
  references: ReferenceWbr[];
  onSubmitFeedback: (draftId: string, section: string, comment: string) => void;
  onApprove: (draftId: string) => void;
  onIngestReference: (title: string) => void;
  onDeleteReference: (id: string) => void;
}

const SECTIONS = [
  "Executive summary",
  "Key metrics",
  "Narrative",
  "Layout",
  "Other",
];

/**
 * Published WBR report rendered in the review preview. Served as a static
 * asset (synced from S3) and displayed in an isolated iframe so its native
 * styling is preserved without clashing with the app theme.
 */
const REPORT_URL = `${process.env.PUBLIC_URL}/reports/SP_Off-Search_WBR_2026-06-06.html`;
const REPORT_TITLE = "SP Off-Search WBR — Detail — Desktop+Smartphone";
const REPORT_WEEK = "Week ending 2026-06-06";

const Drafts: React.FC<DraftsProps> = ({
  team,
  drafts,
  feedback,
  references,
  onSubmitFeedback,
  onApprove,
  onIngestReference,
  onDeleteReference,
}) => {
  const [section, setSection] = useState<string>(SECTIONS[0]);
  const [comment, setComment] = useState("");
  const [tab, setTab] = useState<"preview" | "html">("preview");
  const [refOpen, setRefOpen] = useState(false);
  const [refTitle, setRefTitle] = useState("");
  const [reportHtml, setReportHtml] = useState<string>("");

  useEffect(() => {
    let active = true;
    fetch(REPORT_URL)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((t) => {
        if (active) setReportHtml(t);
      })
      .catch(() => {
        if (active) setReportHtml("");
      });
    return () => {
      active = false;
    };
  }, []);

  const ingestRef = () => {
    if (refTitle.trim()) {
      onIngestReference(refTitle.trim());
      setRefTitle("");
      setRefOpen(false);
    }
  };

  const selected = useMemo(() => drafts[0] ?? null, [drafts]);
  const draftFeedback = useMemo(
    () => (selected ? feedback.filter((f) => f.draftId === selected.id) : []),
    [feedback, selected]
  );

  if (drafts.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            No drafts yet for {team.name}. Trigger a run to generate one.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const submit = () => {
    if (selected && comment.trim()) {
      onSubmitFeedback(selected.id, section, comment.trim());
      setComment("");
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Draft review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Review AI-generated drafts and submit corrections. Feedback feeds back
        into the next run.
      </Typography>

      <Grid container spacing={2.5}>
        {/* Preview */}
        <Grid item xs={12} md={9}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">{REPORT_TITLE}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {REPORT_WEEK}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={tab}
                  onChange={(_, v) => v && setTab(v)}
                >
                  <ToggleButton value="preview">Preview</ToggleButton>
                  <ToggleButton value="html">HTML</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {tab === "preview" &&
                (reportHtml ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "#fff",
                      boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
                    }}
                  >
                    <Box
                      component="iframe"
                      title={REPORT_TITLE}
                      srcDoc={reportHtml}
                      sx={{
                        width: "100%",
                        height: 640,
                        border: 0,
                        display: "block",
                      }}
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 4, textAlign: "center" }}
                  >
                    Loading report…
                  </Typography>
                ))}

              {tab === "html" && (
                <Box
                  component="pre"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    bgcolor: "#1c2235",
                    color: "#cdd3e6",
                    maxHeight: 640,
                    overflow: "auto",
                    fontSize: "0.8rem",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {reportHtml || "Loading report…"}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Feedback panel */}
        <Grid item xs={12} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Feedback
              </Typography>

              {selected && selected.status !== "APPROVED" && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={() => onApprove(selected.id)}
                  sx={{ mb: 2 }}
                >
                  Approve draft
                </Button>
              )}

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Section"
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                >
                  {SECTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Comment"
                  placeholder="Describe the correction…"
                  multiline
                  minRows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <Button
                  variant="contained"
                  startIcon={<SendRoundedIcon />}
                  onClick={submit}
                  disabled={!comment.trim()}
                >
                  Submit feedback
                </Button>
              </Stack>

              <Divider sx={{ mb: 1.5 }} />

              <List dense disablePadding>
                {draftFeedback.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No feedback yet.
                  </Typography>
                )}
                {draftFeedback.map((f) => {
                  const automated = f.source === "REVIEWER_DAG";
                  return (
                    <ListItem
                      key={f.id}
                      alignItems="flex-start"
                      disableGutters
                      sx={{ py: 1 }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          mr: 1.5,
                          fontSize: 13,
                          bgcolor: automated
                            ? "secondary.main"
                            : "primary.light",
                        }}
                      >
                        {automated ? (
                          <SmartToyRoundedIcon sx={{ fontSize: 16 }} />
                        ) : (
                          f.author.slice(0, 2).toUpperCase()
                        )}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{f.author}</strong>{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              · {automated ? "Reviewer DAG" : f.section} ·{" "}
                              {relativeTime(f.createdAt)}
                            </Typography>
                          </Typography>
                        }
                        secondary={f.comment}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reference WBRs — exemplar reports that ground the AI's style and
          structure. Submitted alongside review feedback (see architecture WF3). */}
      <Card sx={{ mt: 2.5 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="h6">Reference WBRs</Typography>
              <Typography variant="body2" color="text.secondary">
                Exemplar reports for {team.name}. These ground the AI's style
                and structure on the next run.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setRefOpen(true)}
            >
              Ingest reference
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {references.map((r) => (
              <Box
                key={r.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{ bgcolor: "secondary.light", color: "secondary.dark" }}
                >
                  <HistoryEduRoundedIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">{r.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.source} · {r.sizeKb} KB · ingested{" "}
                    {formatDateTime(r.ingestedAt)}
                  </Typography>
                </Box>
                <Tooltip title="Remove reference">
                  <IconButton
                    onClick={() => onDeleteReference(r.id)}
                    size="small"
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            {references.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No reference WBRs ingested yet. Add one to improve draft
                quality.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={refOpen}
        onClose={() => setRefOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ingest reference WBR</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide a title for the reference report. In production this is
            paired with an uploaded HTML/Excel body stored in S3 and parsed into
            structured context.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Reference title"
            value={refTitle}
            onChange={(e) => setRefTitle(e.target.value)}
            placeholder="e.g. OS WBR — FY26 Q1 exemplar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={ingestRef}
            disabled={!refTitle.trim()}
          >
            Ingest
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Drafts;
